import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { shouldIgnorePlatformShortcut } from '@/utilities/keyboardShortcuts';

import type { ButtonTabsItem } from '.';

const ROOT = '[data-button-tabs="true"]';
const DIALOG = '[role="dialog"], [role="alertdialog"]';
let lastInteracted: HTMLElement | undefined;

function visible(element: Element): boolean {
    if (element.closest('[hidden], [aria-hidden="true"], [inert]')) {
        return false;
    }
    for (let current: Element | null = element; current; current = current.parentElement) {
        const style = getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
            return false;
        }
    }
    return true;
}

/** Resolve one visible owner, so nested dialogs and multiple tab groups never switch together. */
function shortcutOwner(event: KeyboardEvent): Element | undefined {
    const dialog = [...document.querySelectorAll(DIALOG)].filter(visible).at(-1);
    const candidates = [...document.querySelectorAll(ROOT)].filter(
        (root) => visible(root) && (!dialog || root.closest(DIALOG) === dialog),
    );
    const target = event.target instanceof Element ? event.target : document.activeElement;
    const focused = target?.closest(ROOT);
    if (focused && candidates.includes(focused)) {
        return focused;
    }
    if (lastInteracted && candidates.includes(lastInteracted)) {
        return lastInteracted;
    }
    return candidates.length === 1 ? candidates[0] : undefined;
}

export function useButtonTabsShortcuts(
    rootRef: RefObject<HTMLDivElement>,
    items: readonly ButtonTabsItem[],
    onSelect: (key: string) => void,
): void {
    const latest = useRef({ items, onSelect });
    useEffect(() => {
        latest.current = { items, onSelect };
    });
    useEffect(() => {
        const root = rootRef.current;
        if (!root) {
            return;
        }
        const remember = (event: Event) => {
            if (event.target instanceof Element && event.target.closest(ROOT) === root) {
                lastInteracted = root;
            }
        };
        const handle = (event: KeyboardEvent) => {
            if (
                !event.altKey ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.repeat ||
                shouldIgnorePlatformShortcut(event)
            ) {
                return;
            }
            // Option changes event.key to symbols on macOS; physical digit codes remain stable.
            const digit = /^Digit[1-9]$/.test(event.code)
                ? Number(event.code.slice(-1))
                : /^[1-9]$/.test(event.key)
                  ? Number(event.key)
                  : 0;
            const item = latest.current.items[digit - 1];
            if (!item || item.disabled || shortcutOwner(event) !== root) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            latest.current.onSelect(item.key);
            const button = root.querySelectorAll<HTMLButtonElement>(':scope > [role="tablist"] [role="tab"]')[
                digit - 1
            ];
            button?.focus({ preventScroll: true });
            button?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        };
        root.addEventListener('pointerover', remember);
        root.addEventListener('focusin', remember);
        document.addEventListener('keydown', handle, true);
        return () => {
            root.removeEventListener('pointerover', remember);
            root.removeEventListener('focusin', remember);
            document.removeEventListener('keydown', handle, true);
            if (lastInteracted === root) {
                lastInteracted = undefined;
            }
        };
    }, [rootRef]);
}
